import React, { ChangeEvent, useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';
import { FormControl, Box } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import chevron from '../../asset/images/icons/es_chevron_down.svg';
import { CustomTextField } from '../TextInput/CustomTextField';
import { IFormState } from '../EsContestSubmission/EsContestSubmission';
import { SelectTestUsersModal } from './SelectTestUsersModal';
import CustomButton from '../Button/CustomButton';
import { SelectEmailType } from './SelectEmailType';
import { ContestEmailImageSelector } from './EmailImageSelector/ContestEmailImageSelector';
import { EMAIL_TYPE } from '../../data/forms/project.data';

export interface ISendCampaignEmailProps {
    onSubmit: (formData: IFormState) => void;
    onChange?: (e?: ChangeEvent<HTMLInputElement>) => void;
    emailImages: string[];
}

const SendCampaignEmailModal: React.FC<ISendCampaignEmailProps> = ({ emailImages, onSubmit }) => {
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    const [formState, setFormState] = useState({
        subject: '',
        image: '',
        message: '',
        testUsers: [],
        type: [],
    });
    const updateForm = useCallback((field: string, value: any) => {
        setFormState(prevState => ({
            ...prevState,
            [field]: value,
        }));
    }, []);

    const emailType =
        formState.type && formState.type.length > 0
            ? EMAIL_TYPE.find(type => type.title === formState.type[0])?.title
            : null;

    const isFormDisabled = () => {
        if (emailType === 'contest submission notification email') {
            return !formState.subject || !formState.image;
        } else if (emailType === 'contest notification email') {
            return !formState.subject || !formState.image || !formState.message;
        } else if (emailType === 'voting reminder email') {
            return false;
        } else if (emailType === 'send contest open email') {
            return !formState.image;
        } else {
            return true; // Default return if emailType doesn't match any case
        }
    };

    const handleSetContestImage = (imageString?: string) => {
        updateForm('image', imageString || '');
    };

    const handleSendEmail = () => {
        onSubmit(formState);
    };

    const handleSetEmailType = (emailType?: string[]) => {
        updateForm('type', emailType);
    };
    return (
        <>
            <form style={styles.form}>
                <FormControl style={styles.flexColumn} sx={{ gap: '24px' }}>
                    {/** Sets Email type */}
                    <SelectEmailType
                        onChange={emailType => {
                            handleSetEmailType(emailType);
                        }}
                    />
                    {/* Subject */}
                    {emailType &&
                        ['contest submission notification email', 'contest notification email'].includes(emailType) && (
                            <Box style={styles.flexColumn} sx={{ gap: '8px' }}>
                                <CustomTextField
                                    label="Subject"
                                    labelStyle={{
                                        fontFamily: FONT_ENUM.MEDIUM_HEADER,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 15,
                                    }}
                                    textFieldProps={{
                                        InputProps: {
                                            disableUnderline: true,
                                            value: formState['subject'],
                                            onChange: e => updateForm('subject', e.target.value),
                                            style: {},
                                        },
                                    }}
                                />
                            </Box>
                        )}

                    {/* Sets Contest Image */}
                    {emailType &&
                        [
                            'contest submission notification email',
                            'contest notification email',
                            'send contest open email',
                        ].includes(emailType) && (
                            <Box
                                style={styles.flexRowStart}
                                sx={{
                                    alignItems: 'center',
                                    width: '100%',
                                }}>
                                <ContestEmailImageSelector
                                    options={''}
                                    value={formState['image']}
                                    label="Select an image"
                                    onChange={handleSetContestImage}
                                    emailImages={emailImages}
                                />
                            </Box>
                        )}
                    {/** Sets Test users */}
                    <SelectTestUsersModal
                        value={formState['testUsers']}
                        onChange={users => updateForm('testUsers', users)}
                    />

                    {/* Email Message */}
                    {emailType === 'contest notification email' && (
                        <Box style={styles.flexColumn} sx={{ gap: '8px' }}>
                            <CustomTextField
                                label="Message"
                                labelStyle={{
                                    fontFamily: FONT_ENUM.MEDIUM_HEADER,
                                    color: theme[themeStyle].tag_white,
                                    fontSize: 15,
                                }}
                                textFieldProps={{
                                    InputProps: {
                                        disableUnderline: true,
                                        placeholder: 'Enter email message...',
                                        value: formState['message'],
                                        onChange: e => updateForm('message', e.target.value),
                                    },
                                    multiline: true,
                                    rows: 5,
                                    type: 'text',
                                    style: styles.input,
                                    inputProps: {
                                        style: {
                                            color: theme[themeStyle].lightWhite,
                                            height: '100%',
                                            paddingRight: '14px',
                                            paddingLeft: '14px',
                                            paddingTop: '14px',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    )}
                </FormControl>
            </form>
            {/** Handle Submission */}
            <Box
                sx={{
                    width: '100%',
                    height: 'fit-content',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    mt: 2,
                }}>
                <CustomButton
                    label="Send Email"
                    onClick={handleSendEmail}
                    disabled={isFormDisabled()}
                    sx={{ width: '100%' }}
                />
            </Box>
        </>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        form: {
            height: 'fit-content',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 40,
        },
        text: {
            textAlign: 'left',
            fontStyle: 'normal',
            fontWeight: '400',
            fontFamily: FONT_ENUM.MEDIUM_HEADER,
            color: theme[colorScheme].tag_white,
            fontSize: 15,
        },
        additional: {
            marginBottom: 2,
        },
        input: {
            width: '100%',
            paddingLeft: 14,
            paddingRight: 14,
            backgroundColor: theme[colorScheme].gray_text,
            borderRadius: 8,
            border: 'none',
        },
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            height: 'fit-content',
            width: '100%',
        },
        flexRowBetween: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
            paddingTop: '10px',
            paddingRight: '14px',
            paddingLeft: '14px',
            paddingBottom: '10px',
            borderRadius: 8,
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            borderRadius: 8,
        },
        option: {
            color: theme[colorScheme].lightWhite,
            fontSize: 16,
            cursor: 'pointer',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            fontWeight: '500',
        },
        absolute: {
            position: 'absolute',
            top: '0px',
            right: '0px',
            left: '0px',
            width: 'inherit',
            height: 'auto',
            zIndex: 100,
            backgroundColor: theme[colorScheme].gray_text,
        },
        datalist: {
            backgroundColor: theme[colorScheme].gray_text,
            height: '100%',
            width: '100%',
            border: 'none',
            borderRadius: 8,
            WebkitCalendarPickerIndicator: 'none',
            color: theme[colorScheme].lightWhite,
            backgroundImage: `url(${chevron})`,
            backgroundSize: '20px',
            paddingRight: '25px',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right',
            position: 'relative',
        },
        or: {
            textAlign: 'left',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '500',
            color: theme[colorScheme].form_label,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        line: {
            width: '100%',
        },
        videoContainer: {
            width: '100%',
            backgroundColor: theme[colorScheme].gray_text,
            position: 'relative',
            height: '200px',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        hide: {
            width: 'inherit',
            height: 'inherit',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            opacity: 0,
        },
        video: {
            width: 'inherit',
            height: 'inherit',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
        },
        detailsButton: {
            height: '52px',
            borderRadius: 100,
            fontSize: 16,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '56px',
        },
        flexRowStart: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        },
        addFile: {
            borderRadius: 100,
            fontSize: 10,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            color: theme[colorScheme].tag_white,
        },
        addFileSub: {
            borderRadius: 100,
            fontSize: 8,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            color: theme[colorScheme].tag_white,
        },
    });

export default SendCampaignEmailModal;
